/**
 * Mock payouts — one per completed booking, with realistic status distribution
 * (paid, pending). Powers the provider's Earnings page.
 */

import type { Tables } from '@/lib/db.types'
import { payoutEntityId } from './ids'
import { MOCK_BOOKINGS } from './bookings'

type Payout = Tables<'payouts'>

const NOW = Date.parse('2026-05-01T08:00:00.000Z')

export const MOCK_PAYOUTS: Payout[] = MOCK_BOOKINGS
  .filter((b) => b.status === 'completed' || b.status === 'in_progress')
  .map((b, i) => {
    const n = i + 1
    const completedAt = b.completed_at ? Date.parse(b.completed_at) : Date.parse(b.scheduled_at)
    // Pay out 3 days after completion in mock-land
    const paidAt = completedAt + 3 * 86400_000
    const isPaid = paidAt < NOW && b.status === 'completed'
    return {
      id: payoutEntityId(n),
      booking_id: b.id,
      provider_id: b.provider_id,
      amount: b.provider_payout,
      currency: 'SEK',
      status: isPaid ? 'paid' : 'pending',
      stripe_transfer_id: isPaid ? `tr_mock_${n.toString().padStart(6, '0')}` : null,
      paid_at: isPaid ? new Date(paidAt).toISOString() : null,
      failed_reason: null,
      created_at: new Date(completedAt).toISOString(),
    } satisfies Payout
  })

export function findPayoutsByProviderUserId(_userId: string): Payout[] {
  // usePayouts filters by user.id, but our mock provider_id is the provider entity id.
  // For the dev session we map "provider user_id" → "provider entity id" via the
  // DEV_PROVIDER fixture; for the mock list, simply expose every dev-provider payout.
  return MOCK_PAYOUTS
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
}

export function findPayoutsByProviderEntityId(providerEntityId: string): Payout[] {
  return MOCK_PAYOUTS
    .filter((p) => p.provider_id === providerEntityId)
    .sort((a, b) => Date.parse(b.created_at) - Date.parse(a.created_at))
}
